import { FC, ReactNode } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import {
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByDeliveryAddressIdConsumption,
  ConsumptionResponse,
  ConsumptionTypeDto,
  DisplayModeDto,
  TimeFrameDto,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { Box } from '@eneco/sparky';

import DataErrorNotification from '../../../../components/DataErrorNotification';
import { useAddressOptions } from '../../../../hooks/useAddressOptions';
import { useServiceDeliveryPoints } from '../../../../hooks/useServiceDeliveryPoints';

interface StandardConsumptionDataProps {
  consumptionType: Exclude<ConsumptionTypeDto, 'PeakValues'>;
  timeFrame: TimeFrameDto;
  startDate: string;
  endDate: string;
  children: (data: { consumptionData: ConsumptionResponse | null; isLoading: boolean }) => ReactNode;
  showSmr3SwitchCta: boolean;
  isGraph?: boolean;
  signal?: AbortSignal;
}

const StandardConsumptionData: FC<StandardConsumptionDataProps> = ({
  consumptionType,
  timeFrame,
  startDate,
  endDate,
  children,
  showSmr3SwitchCta,
  isGraph = false,
  signal,
}) => {
  const { data: session } = useSession();
  const {
    selectedAccount: { crmAccountNumber: accountNumber },
  } = useSelfServiceAccount();
  const { addressIdentifier } = useAddressOptions();
  const { serviceDeliveryPointsData } = useServiceDeliveryPoints();

  const energyType = consumptionType === 'Injection' ? 'Electricity' : consumptionType;
  const meterNumbers = (serviceDeliveryPointsData?.serviceDeliveryPoints ?? [])
    .filter(sdp => energyType === 'Total' || sdp.type === energyType)
    .map(sdp => sdp.meterNumber)
    .filter((meterNumber): meterNumber is string => !!meterNumber);

  const query = {
    consumptionType,
    displayMode: 'Consumption' as DisplayModeDto,
    timeFrame,
    startDate,
    endDate,
    meterNumber: meterNumbers,
  };

  const { data, isLoading, error } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByDeliveryAddressIdConsumption,
    {
      path: {
        accountNumber,
        deliveryAddressId: addressIdentifier,
      },
      query,
      signal,
    },
    [
      `/accounts/${accountNumber}/delivery-addresses/${addressIdentifier}/consumption`,
      timeFrame,
      startDate,
      endDate,
      consumptionType,
      meterNumbers.join(','),
    ],
    session,
    { shouldFetch: !showSmr3SwitchCta },
  );

  const consumptionData = unwrapData(data);

  if (error) {
    if (isGraph) {
      return (
        <Box paddingLeft="6" paddingTop="6">
          <DataErrorNotification />
        </Box>
      );
    }
    return <DataErrorNotification />;
  }

  return <>{children({ consumptionData, isLoading })}</>;
};

export default StandardConsumptionData;
